/**
 * What the library draws, as plain functions (`03-client-app.md` section 4.2).
 *
 * The screen is the Epic Games Store library with the divisions taken out: one grid of capsules
 * holding every game the account can open, with the name under each, a toolbar that searches and
 * sorts it, and a menu of actions on every tile. None of that is hard; what is hard is that every
 * tile has to answer "what is this game doing right now" from three sources at once — what is
 * installed on disk, what the download queue is working on, and what the API says has a newer
 * build — and answer it the same way for fifty tiles at a time.
 *
 * So the answer is worked out here, once, and tested. A tile is then only a picture of a
 * `TileView`, and the grid is only a sorted list of them.
 *
 * The matching and the alphabet at the foot of this file are not the library's alone: Games and
 * Search look through the same catalogue and borrow them, so a word that finds a game on one
 * screen finds it on all three.
 *
 * The one piece of this that is not bookkeeping is `colour`. A game that is not on this machine
 * is drawn in grey, and installing it fills the artwork back in from the left as the bytes
 * land, so a shelf read from across the room says what is here and what is arriving without a
 * word of text laid over anybody's picture.
 */

import type { Game } from '@goodbuild/shared';
import type { InstallState } from '../components/game/useInstallState.js';
import { downloadNumbers } from '../downloads/progress.js';
import type { GameJob } from '../downloads/store.js';

/** The line under the name. Short, because it sits in a column of fifty of them. */
const STATE_LABELS: Record<InstallState, string> = {
  unavailable: 'Not on this device',
  locked: 'Included with a membership',
  not_installed: 'Not installed',
  queued: 'Queued',
  downloading: 'Downloading',
  installing: 'Installing',
  paused: 'Paused',
  verifying: 'Verifying',
  uninstalling: 'Removing',
  installed: 'Installed',
  update_available: 'Update available',
  running: 'Running',
  error: 'Stopped',
};

/** The states with a job behind them, which are the ones a bar is drawn for. */
const BUSY: ReadonlySet<InstallState> = new Set<InstallState>([
  'queued',
  'downloading',
  'installing',
  'paused',
  'verifying',
  'uninstalling',
]);

/** The states that mean the game is on this machine, whatever else is happening to it. */
const ON_DISK: ReadonlySet<InstallState> = new Set<InstallState>([
  'installed',
  'update_available',
  'running',
  'verifying',
  'uninstalling',
]);

export interface TileInput {
  /** A published build exists for this device. False dims the tile past grey. */
  availableHere: boolean;
  /** A membership or staff. Games are not sold one at a time, so this is the only thing that
   * opens a tile. */
  hasAccess: boolean;
  /** The game is in `games_installed`, whatever the queue is doing to it. */
  installed: boolean;
  hasUpdate: boolean;
  /** This game's row in the downloads store, when it has one. */
  job: GameJob | null;
}

export interface TileView {
  state: InstallState;
  /**
   * How much of the artwork is in colour, 0 to 1.
   *
   * 0 is a grey tile — a game that exists and is not here. 1 is a game on this machine. In
   * between is a download in flight, and it is the fraction of the job rather than anything
   * decorative: the colour arriving is the build arriving.
   */
  colour: number;
  /** The bar along the foot of the tile: 0 to 1, or null with nothing measured yet. */
  fraction: number | null;
  percent: number | null;
  label: string;
  /** True while a job is working on this game, which is what puts a bar on the tile. */
  busy: boolean;
}

/**
 * Which state a tile is in, from the three things that know.
 *
 * The order is the InstallButton's order (`useInstallState.ts`) and has to stay it: a tile
 * saying Install over a running download, or Play over a game the account no longer has access
 * to, is the same bug in a smaller box.
 */
function resolveState(input: TileInput): InstallState {
  const { job } = input;
  if (!input.availableHere) return 'unavailable';

  // A job that stopped because the network went away comes back paused with the reason on it
  // (`04-launcher-and-patcher.md` section 2.3). That is a pause with something to say, not a
  // failure, so it is not drawn as one.
  if (job?.error && job.state !== 'paused') return 'error';

  switch (job?.state) {
    case 'running':
    case 'queued':
    case 'downloading':
    case 'paused':
    case 'installing':
    case 'verifying':
    case 'uninstalling':
      return job.state;
    case 'locked':
      return 'locked';
    default:
      break;
  }

  // Either one opens it. A membership covers everything; a purchase covers this game and is not
  // undone by the membership ending, so both have to be asked.
  if (!input.hasAccess) return 'locked';
  if (!input.installed) return 'not_installed';
  if (input.hasUpdate) return 'update_available';
  return 'installed';
}

export function tileStatus(input: TileInput): TileView {
  const state = resolveState(input);
  const numbers = downloadNumbers(input.job?.progress ?? null, input.job?.queued ?? null);
  const busy = BUSY.has(state);

  // An update is a download over a game that is already here, and greying the artwork back out
  // to fill it in again would read as the game being removed. Colour only ever climbs.
  const colour = ON_DISK.has(state) || input.installed ? 1 : busy ? (numbers.fraction ?? 0) : 0;

  const label =
    busy && numbers.percent !== null && state !== 'queued'
      ? `${STATE_LABELS[state]} ${numbers.percent}%`
      : STATE_LABELS[state];

  return {
    state,
    colour,
    fraction: busy ? numbers.fraction : null,
    percent: numbers.percent,
    label,
    busy,
  };
}

/* ------------------------------------------------------------------ actions */

/**
 * What the tile menu offers.
 *
 * Epic hangs this off a "…" in the corner of every capsule, and it is the right place for it:
 * installing, pausing and uninstalling are things you do to a game in a list of games, not
 * things worth opening a store page for. On a controller it is X, because that is the secondary
 * action everywhere else in this app.
 *
 * `reveal` hands the install folder to the desktop's file manager: saves, logs, screenshots and
 * a mod folder all live in there, and every one of them is a reason to go and look that the app
 * itself has no screen for.
 */
export type LibraryAction =
  | 'open'
  | 'install'
  | 'update'
  | 'play'
  | 'pause'
  | 'resume'
  | 'cancel'
  | 'verify'
  | 'reveal'
  | 'uninstall'
  | 'downloads'
  | 'join'
  | 'app';

export interface ActionItem {
  id: LibraryAction;
  label: string;
  /** Drawn in the danger colour and never the first item, so it is not the one you hit. */
  danger?: boolean;
}

const OPEN: ActionItem = { id: 'open', label: 'Game page' };

const REVEAL: ActionItem = { id: 'reveal', label: 'Open folder' };

/**
 * The states with a folder on this machine worth opening.
 *
 * `uninstalling` is the one on-disk state left out: the folder is being emptied, so a file
 * manager pointed at it is a window watching something disappear.
 */
const REVEALABLE: ReadonlySet<InstallState> = new Set<InstallState>([
  'installed',
  'update_available',
  'running',
  'verifying',
]);

/**
 * The menu for a state, primary action first.
 *
 * `canInstall` is false in a browser, where none of this is possible: the menu there is the
 * page and the pitch for the app, rather than five items that would all fail. `canReveal` is
 * false where there is no file manager to hand a folder to (`platform/index.ts`).
 */
export function tileActions(
  state: InstallState,
  canInstall: boolean,
  canReveal = false,
): ActionItem[] {
  const items = baseActions(state, canInstall);
  // `canInstall` as well, and not only because the web build reveals nothing: a device that
  // cannot install a game has no folder for one either, whatever it can open.
  if (!canInstall || !canReveal || !REVEALABLE.has(state)) return items;

  // Beside the game page, which is the other item that is about the game rather than about the
  // download — and never first, because a menu whose default press throws a file manager over
  // the game somebody is playing has picked the wrong default. A running game's menu is the
  // page alone, so there the two swap round.
  const at = Math.max(
    items.findIndex((item) => item.id === 'open'),
    1,
  );
  return [...items.slice(0, at), REVEAL, ...items.slice(at)];
}

function baseActions(state: InstallState, canInstall: boolean): ActionItem[] {
  if (!canInstall) return [{ id: 'app', label: 'Get the app to play' }, OPEN];

  switch (state) {
    case 'unavailable':
    case 'running':
    case 'verifying':
    case 'uninstalling':
      return [OPEN];
    case 'locked':
      return [{ id: 'join', label: 'Become a supporter' }, OPEN];
    case 'not_installed':
      return [{ id: 'install', label: 'Install' }, OPEN];
    case 'error':
      return [{ id: 'install', label: 'Try again' }, OPEN];
    case 'queued':
      return [
        { id: 'cancel', label: 'Cancel', danger: true },
        { id: 'downloads', label: 'All downloads' },
        OPEN,
      ];
    case 'downloading':
    case 'installing':
      return [
        { id: 'pause', label: 'Pause' },
        { id: 'downloads', label: 'All downloads' },
        { id: 'cancel', label: 'Cancel', danger: true },
        OPEN,
      ];
    case 'paused':
      return [
        { id: 'resume', label: 'Resume' },
        { id: 'downloads', label: 'All downloads' },
        { id: 'cancel', label: 'Cancel', danger: true },
        OPEN,
      ];
    case 'update_available':
      return [
        { id: 'update', label: 'Update' },
        OPEN,
        { id: 'uninstall', label: 'Uninstall', danger: true },
      ];
    case 'installed':
      return [
        { id: 'play', label: 'Play' },
        { id: 'verify', label: 'Verify files' },
        OPEN,
        { id: 'uninstall', label: 'Uninstall', danger: true },
      ];
  }
}

/**
 * What the tile itself does when it is picked.
 *
 * Epic's library opens a page; ours does the thing. A library is a room full of games somebody
 * has already chosen, so the one gesture it owes them is the short way back into one: press the
 * capsule and it plays, or starts arriving if it is not here yet. The page is still one press
 * further on, on the "…", for the day what you want is the notes and the screenshots.
 *
 * It is the first item of `tileActions` in every state where that item is something worth
 * having on a single press, and the game page in the rest. A tile mid-download is where the two
 * part: the menu leads with Pause because that is a useful thing to pick deliberately, and a
 * whole capsule that stops a download because somebody clicked the wrong picture is not. That
 * one goes to the downloads screen, where the bytes and the queue already are.
 */
export function tilePrimary(state: InstallState, canInstall: boolean): ActionItem {
  // A browser installs nothing and launches nothing, so the page — which carries the pitch for
  // the app — is the only thing a press there can honestly lead to.
  if (!canInstall) return OPEN;

  switch (state) {
    // Taken from the menu rather than written out again, so the press and the first item of the
    // "…" can never drift into saying two different things about the same tile.
    case 'not_installed':
    case 'error':
    case 'locked':
    case 'installed':
    case 'update_available':
    case 'paused':
      return tileActions(state, canInstall)[0]!;
    case 'queued':
    case 'downloading':
    case 'installing':
      return { id: 'downloads', label: 'All downloads' };
    // Running, being checked, being removed, or not made for this machine: nothing a press
    // could start that has not started already, so it reads about the game instead.
    case 'unavailable':
    case 'running':
    case 'verifying':
    case 'uninstalling':
      return OPEN;
  }
}

/* ------------------------------------------------- searching and arranging */

/** Case and punctuation insensitive, so "cairnfall" finds "Cairnfall" and "tidal-remains". */
export function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Whether a game answers to a query that has already been through `normalise`. */
export function matchesQuery(game: Game, needle: string): boolean {
  if (!needle) return true;
  const hay = [
    game.title,
    game.shortDescription,
    game.slug,
    game.developerName ?? '',
    ...game.genres,
    ...game.devs.map((dev) => dev.displayName ?? dev.username),
  ];
  return hay.some((value) => normalise(value).includes(needle));
}

export const LIBRARY_SORTS = ['alpha', 'recent', 'updated', 'size'] as const;
export type LibrarySort = (typeof LIBRARY_SORTS)[number];

export const LIBRARY_SORT_LABELS: Record<LibrarySort, string> = {
  alpha: 'Alphabetical',
  recent: 'Recently played',
  updated: 'Recently updated',
  size: 'Install size',
};

export function isLibrarySort(value: unknown): value is LibrarySort {
  return typeof value === 'string' && (LIBRARY_SORTS as readonly string[]).includes(value);
}

/** One game, with everything the grid needs to place it. */
export interface LibraryEntry {
  game: Game;
  view: TileView;
  /** When it was installed here, or null for a game that is not. */
  installedAt: number | null;
  /** The size on disk, or null when it is not installed. */
  sizeBytes: number | null;
  /** When it was last launched from this app. Null for a game nobody has played here. */
  lastPlayedAt: number | null;
  /** The newest published build across platforms, which is what "recently updated" means. */
  publishedAt: number;
}

/** The newest build a game has, or 0 for a game with none. */
export function newestBuildAt(game: Game): number {
  return game.builds.reduce((newest, build) => Math.max(newest, build.publishedAt), 0);
}

/**
 * The alphabet, as every grid of games in this app spells it.
 *
 * Exported because the catalogue arranges plain games and the library arranges entries around
 * them, and two screens disagreeing about where "The Tidal Remains" goes would be two
 * different alphabets.
 */
export function compareTitles(a: Game, b: Game): number {
  return a.title.localeCompare(b.title, 'en', { sensitivity: 'base' });
}

function byTitle(a: LibraryEntry, b: LibraryEntry): number {
  return compareTitles(a.game, b.game);
}

/** Sorts descending on a number that may be missing, with missing always last. */
function byNewest(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

/**
 * Arranges the grid.
 *
 * Every sort falls back to the alphabet, so two games with nothing to tell them apart keep a
 * stable order rather than swapping places every time the queue ticks.
 */
export function sortEntries(entries: LibraryEntry[], sort: LibrarySort): LibraryEntry[] {
  const sorted = [...entries];
  switch (sort) {
    case 'alpha':
      return sorted.sort(byTitle);
    case 'recent':
      // Played, then merely installed, then the rest. A library sorted by "recent" that put a
      // game nobody has ever installed above one played this morning would be sorted by
      // nothing at all.
      return sorted.sort(
        (a, b) =>
          byNewest(a.lastPlayedAt, b.lastPlayedAt) ||
          byNewest(a.installedAt, b.installedAt) ||
          byTitle(a, b),
      );
    case 'updated':
      return sorted.sort((a, b) => b.publishedAt - a.publishedAt || byTitle(a, b));
    case 'size':
      return sorted.sort((a, b) => byNewest(a.sizeBytes, b.sizeBytes) || byTitle(a, b));
  }
}
